import { useState } from 'react';
import i18next from 'i18next';
import { toast } from "react-toastify";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTitle: Full Stack Developer
 *
 * This component and utility manage face detection using face-api.js.
 * It dynamically loads required scripts, ensures models are ready,
 * and exposes a face validation method that works with both
 * base64 images and Blob/File inputs.
 */

// Utility object to handle face-api.js loading and validation
const faceDetectionManager = {
    loaded: false, // Whether libraries and models are already loaded
    loading: false, // Prevents loading multiple times in parallel
    // Minimum confidence required to accept a face as valid
    MIN_CONFIDENCE: 0.94,
    /**
     * Dynamically load a JavaScript file by creating a <script> tag.
     * @param {string} src - URL of the script to load
     * @returns {Promise<void>}
     */
    loadScript(src) {
        return new Promise((resolve, reject) => {
            // If script already exists, resolve immediately
            if (document.querySelector(`script[src="${src}"]`)) return resolve();

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = resolve; // Resolve when loaded
            script.onerror = reject; // Reject on error
            document.body.appendChild(script);
        });
    },

    /**
     * Wait for `window.faceapi` to become available.
     * Times out after 5 seconds if not found.
     */
    async waitForFaceApi() {
        if (window.faceapi) return; // Already available

        await new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (window.faceapi) {
                    clearInterval(interval);
                    resolve();
                }
            }, 50);

            // Timeout after 5 seconds
            setTimeout(() => {
                clearInterval(interval);
                reject(new Error(i18next.t('something_went_wrong_2')));
            }, 5000);
        });
    },

    /**
     * Load TensorFlow.js, face-api.js, and the TinyFaceDetector model.
     */
    async loadLibraries() {
        if (this.loaded || this.loading) return;
        this.loading = true;

        try {
            // Load TensorFlow.js and face-api.js from CDN
            await this.loadScript('/faceApiLibs/tf.min.js'); //
            await this.loadScript('/faceApiLibs/face-api.min.js'); //

            // Wait until face-api is ready
            await this.waitForFaceApi();

            // Load SsdMobilenetv1 model from public folder
            await window.faceapi.nets.ssdMobilenetv1.loadFromUri('/faceApiModels');

            this.loaded = true;
        } catch (err) {
            toast.error(i18next.t('something_went_wrong_2'));
            throw err;
        } finally {
            this.loading = false;
        }
    },

    /**
     * Run face detection on an image input and return the raw best score.
     * @param {string|File|Blob}  - Base64 string or file/blob
     * @returns {Promise<number|null>} Best detection score, or null if no face
     */
    async detectScore(input) {
        if (!this.loaded) await this.loadLibraries();

        let imgDataUrl;

        // If input is already a base64 image string
        if (typeof input === 'string' && input.startsWith('data:image/')) {
            imgDataUrl = input;
        } else {
            // Otherwise, convert file/blob to base64
            imgDataUrl = await this.readFileAsDataURL(input);
        }

        // Create an Image object from the base64 string
        const img = await this.loadImage(imgDataUrl);

        // Detect with a low threshold so the detector returns its best candidate
        // and we can read the score even when it does not reach MIN_CONFIDENCE
        const detection = await window.faceapi.detectSingleFace(
            img,
            new window.faceapi.SsdMobilenetv1Options({
                minConfidence: 0.01  // Low threshold just to expose the score
            })
        );

        return detection ? detection.score : null;
    },

    /**
     * Validate if a face is detected in the given image.
     * @param {string|File|Blob} input - Base64 string or file/blob
     * @returns {Promise<number>} Number of faces detected
     */
    async validateFace(input) {
        const score = await this.detectScore(input);

        // Apply the real minimum: accept only faces reaching MIN_CONFIDENCE
        return score !== null && score >= this.MIN_CONFIDENCE ? 1 : 0;
    },

    /**
     * Validate every image of a selfie folder and build a text report.
     * Each line holds the image name, its score and a status mark:
     * a check (✓) when the score reaches MIN_CONFIDENCE, an x otherwise.
     * The report is downloaded as a .txt file.
     * @param {FileList|File[]} files - Images selected from the selfie folder
     * @returns {Promise<string>} The generated report content
     */
    async validateSelfieFolder(files) {
        if (!this.loaded) await this.loadLibraries();

        // Keep only image files from the selected folder
        const images = Array.from(files).filter((file) => file.type.startsWith('image/'));

        const lines = [];

        for (const file of images) {
            let score = null;

            try {
                score = await this.detectScore(file);
            } catch (err) {
                // Mark unreadable/failed images so they are visible in the report
                lines.push(`${file.name}\tERROR\tx`);
                continue;
            }

            // No face detected at all
            if (score === null) {
                lines.push(`${file.name}\t0.0000\tx`);
                continue;
            }

            const status = score >= this.MIN_CONFIDENCE ? i18next.t('yes') : i18next.t('no');
            lines.push(`${file.name}\t${score.toFixed(4)}\t${status}`);
        }

        const report = lines.join('\n');
        this.downloadReport(report, 'selfie_validation.txt');

        return report;
    },

    /**
     * Trigger a browser download of the given text content.
     * @param {string} content - Text to download
     * @param {string} fileName - Name of the downloaded file
     */
    downloadReport(content, fileName) {
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    },

    /**
     * Convert a file/blob into a base64 DataURL.
     */
    readFileAsDataURL(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    },

    /**
     * Load an image from a base64 or URL into an Image object.
     */
    loadImage(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Avoid CORS issues
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    }
};

// React hook-based component wrapper
const FaceDetectorApp = () => {
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);

    /**
     * Wrapper method to validate faces, only runs if models are loaded.
     */
    const validateFaceBlobAndBase64 = async (input) => {
        // Show full page loader during the whole validation: library load + face detection
        setIsLoadingLibrary(true);
        try {
            if (!modelsLoaded) {
                await faceDetectionManager.loadLibraries();
                setModelsLoaded(true);
            }

            return await faceDetectionManager.validateFace(input);
        } catch (err) {
            toast.error(i18next.t('something_went_wrong_2'));
            return 0;
        } finally {
            setIsLoadingLibrary(false);
        }
    };

    /**
     * Wrapper to validate every image of the selfies folder and download the report.
     */
    const handleValidateSelfiesFolder = async (files) => {
        setIsLoadingLibrary(true);
        try {
            if (!modelsLoaded) {
                await faceDetectionManager.loadLibraries();
                setModelsLoaded(true);
            }

            return await faceDetectionManager.validateSelfieFolder(files);
        } catch (err) {
            toast.error(i18next.t('something_went_wrong_2'));
            return '';
        } finally {
            setIsLoadingLibrary(false);
        }
    };

    // Returns an API-like object for external use
    return {
        handleValidateFace: validateFaceBlobAndBase64,
        handleValidateSelfiesFolder,
        isLoadingLibrary
    };
};

export default FaceDetectorApp;
